import {buildArchiveQuery} from '../utils/archiveQuery';
import axios from 'axios';

function archiveBasePath() {
    const href = window.location.href;

    if (href.includes('?')) {
        return href.substring(0, href.indexOf('?'));
    }

    if (href.includes('#')) {
        return href.substring(0, href.indexOf('#'));
    }

    return href;
}

function normalizeArchiveResult(result) {
    return {
        applied_filters: Array.isArray(result?.applied_filters) ? result.applied_filters : [],
        available_filters: {
            attributes: Array.isArray(result?.available_filters?.attributes) ? result.available_filters.attributes : [],
            brands: Array.isArray(result?.available_filters?.brands) ? result.available_filters.brands : [],
            categories: Array.isArray(result?.available_filters?.categories) ? result.available_filters.categories : [],
            price: result?.available_filters?.price || {},
        },
        links: result?.links || {},
        meta: result?.meta || {},
        route: result?.route || {},
        data: Array.isArray(result?.data) ? result.data : [],
    };
}

export default {
    namespaced: true,
    state () {
        return {
            loading: false,
            applied_filters: [],
            available_filters: {
                attributes: [],
                brands: [],
                categories: [],
                price: {},
            },
            links: {},
            meta: {},
            route: {
                brands: [],
                categories: [],
                gender: [],
                page: [],
                tag: [],
                title: [],
            },
            request: {
                brands: [],
                categories: [],
                attributes: [],
                min_price: null,
                max_price: null,
                orderby: 'default',
                page: null,
                search: null,
                stock_status: false,
                special_offers: false,
                trigger: 0
            },
            data: [],
            showFilters: false,
        }
    },
    mutations: {
        setResult (state, result) {
            const payload = normalizeArchiveResult(result);

            state.applied_filters = [...payload.applied_filters];
            state.available_filters = {
                attributes: [...payload.available_filters.attributes],
                brands: [...payload.available_filters.brands],
                categories: [...payload.available_filters.categories],
                price: {...payload.available_filters.price},
            };
            state.links = {...payload.links};
            state.meta = {
                ...payload.meta,
                links: [...(payload.meta?.links || [])],
            };
            state.route = {
                brands: [...(payload.route.brands || [])],
                categories: [...(payload.route.categories || [])],
                gender: payload.route.gender || {},
                page: payload.route.page,
                tag: payload.route.tag || {},
                title: payload.route.title,
            };
            state.data = [...payload.data];
            state.request = {
                ...state.request,
                page: payload.meta?.current_page ? Number(payload.meta.current_page) : state.request.page,
            };
        },
        initRequestFromQuery(state, payload) {
            state.request = {
                ...state.request,
                brands: payload.brands || [],
                categories: payload.categories || [],
                attributes: payload.attributes || [],
                min_price: payload.min_price ?? null,
                max_price: payload.max_price ?? null,
                orderby: payload.orderby || 'default',
                page: payload.page ?? null,
                search: payload.search ?? null,
                stock_status: !!payload.stock_status,
                special_offers: !!payload.special_offers,
            };
        },
        addBrand (state, brand) {
            state.request = {
                ...state.request,
                categories: [...state.request.categories],
                attributes: [...state.request.attributes],
                brands: [...state.request.brands, Number(brand)]
            };
        },
        removeBrand (state, brand) {
            state.request = {
                ...state.request,
                categories: [...state.request.categories],
                attributes: [...state.request.attributes],
                brands: [...state.request.brands.filter(_brand => {
                    return _brand !== Number(brand);
                })]
            };
        },
        addAttribute (state, attribute) {
            state.request = {
                ...state.request,
                categories: [...state.request.categories],
                brands: [...state.request.brands],
                attributes: [...state.request.attributes, Number(attribute)]
            };
        },
        removeAttribute (state, attribute) {
            state.request = {
                ...state.request,
                categories: [...state.request.categories],
                brands: [...state.request.brands],
                attributes: [...state.request.attributes.filter(_attribute => {
                    return _attribute !== Number(attribute);
                })]
            };
        },
        setFilterValue (state, payload) {
            const newObject = {};
            newObject[payload[0]] = payload[1];
            state.request = {
                ...state.request,
                ...newObject
            }
        },
        setPage (state, page) {
            state.request = {
                ...state.request,
                page: page ? Number(page) : null,
            };
        },
        setOrderby (state, orderby) {
            state.request = {
                ...state.request,
                orderby: orderby || 'default',
            };
        },
        setLoading (state, loading) {
            state.loading = loading;
        },
        fireTrigger (state) {
            state.request = {
                ...state.request,
                trigger: state.request.trigger + 1,
            };
        },
        setShowFilters(state, showFilters) {
            state.showFilters = showFilters;
        },
        clearFilters(state) {
            const routeBrandIds = (state.route.brands || []).map((brand) => Number(brand.id)).filter(Boolean);
            const routeGenderId = state.route.gender?.id ? [Number(state.route.gender.id)] : [];

            state.request = {
                ...state.request,
                brands: [...routeBrandIds],
                categories: [],
                attributes: [...routeGenderId],
                min_price: null,
                max_price: null,
                search: null,
                stock_status: false,
                special_offers: false,
                page: 1,
            };
        },
    },
    actions: {
        clearAllFilters({commit, dispatch}) {
            commit('clearFilters');
            dispatch('fetchResults', {page: 1});
        },
        async fetchResults({state, commit}, payload = {}) {
            if (payload.orderby !== undefined) {
                commit('setOrderby', payload.orderby);
            }
            if (payload.page !== undefined) {
                commit('setPage', payload.page);
            }

            commit('fireTrigger');
            commit('setLoading', true);

            const basePath = archiveBasePath();
            const query = buildArchiveQuery(state.request);
            const url = basePath + (query ? '?' + query : '');

            try {
                const {data} = await axios.get(url, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                if (!data || !Array.isArray(data.data)) {
                    throw new Error('Archive response is not valid JSON');
                }

                commit('setResult', data);
                window.history.pushState(null, '', url);
                window.scrollTo({top: 0, behavior: 'smooth'});
            } catch (error) {
                console.error('Archive fetch failed:', error?.response?.status, error?.response?.data || error);
                window.scrollTo({top: 0, behavior: 'smooth'});
            } finally {
                commit('setLoading', false);
            }
        },
    },
    getters: {

    }
}
